import type { OnboardingRecommendationAnswers } from "@repo/types";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import {
  deriveTemplateAssetIds,
  normalizeTokenMap,
  toRecord,
} from "~/features/portfolio-setup";
import { queryKeys } from "~/lib/query-keys";
import { getBrowserPlatformApiClient } from "~/lib/trpc/browser-client";
import type { PlatformApiTrpcClient } from "~/lib/trpc/platform-api-adapter";

export type OnboardingV1RecommendationsOutput = Awaited<
  ReturnType<
    PlatformApiTrpcClient["v2"]["portfolio"]["builder"]["recommendForOnboardingAnswers"]["query"]
  >
>;
type OnboardingV1TokenDataOutput = Awaited<
  ReturnType<
    PlatformApiTrpcClient["assets"]["getBasicMarketDataForAssets"]["query"]
  >
>;

export interface OnboardingV1RecommendationDataSource {
  readonly loadRecommendations: (
    answers: OnboardingRecommendationAnswers,
  ) => Promise<OnboardingV1RecommendationsOutput>;
  readonly loadTokenData: (
    assetIds: readonly string[],
  ) => Promise<OnboardingV1TokenDataOutput>;
}

export type OnboardingV1RecommendationCard =
  OnboardingV1RecommendationsOutput["recommendations"][number] & {
    readonly tokens: ReadonlyArray<{
      readonly symbol: string;
      readonly url: string;
    }>;
  };

const browserDataSource: OnboardingV1RecommendationDataSource = {
  loadRecommendations: async (answers) => {
    const platformApi = getBrowserPlatformApiClient();
    return await platformApi.v2.portfolio.builder.recommendForOnboardingAnswers.query(
      { answers, prompt: null },
    );
  },
  loadTokenData: async (assetIds) => {
    const platformApi = getBrowserPlatformApiClient();
    return await platformApi.assets.getBasicMarketDataForAssets.query({
      assetIds: [...assetIds],
    });
  },
};

function getRecommendationQueryOptions(input: {
  readonly answers: OnboardingRecommendationAnswers;
  readonly dataSource: OnboardingV1RecommendationDataSource;
}) {
  return queryOptions({
    queryFn: () => input.dataSource.loadRecommendations(input.answers),
    queryKey:
      queryKeys.platformApi.portfolioBuilderRecommendForOnboardingAnswers(
        input.answers,
      ),
    retry: false,
    staleTime: 30_000,
  });
}

function getRecommendationAssetIds(
  data: OnboardingV1RecommendationsOutput | undefined,
): string[] {
  const ids = new Set<string>();
  for (const recommendation of data?.recommendations ?? []) {
    for (const assetId of deriveTemplateAssetIds(recommendation.strategyData)) {
      ids.add(assetId);
    }
  }
  return Array.from(ids);
}

function getTokenDataQueryOptions(input: {
  readonly assetIds: readonly string[];
  readonly dataSource: OnboardingV1RecommendationDataSource;
}) {
  return queryOptions({
    queryFn: () => input.dataSource.loadTokenData(input.assetIds),
    queryKey: queryKeys.start.builderTokenData(input.assetIds),
    retry: false,
    staleTime: 5 * 60 * 1_000,
  });
}

function getTokenImageUrl(
  token: ReturnType<typeof normalizeTokenMap>[string]["token"],
): string | null {
  return (
    token?.info?.imageSmallUrl ??
    token?.info?.imageThumbUrl ??
    token?.info?.imageLargeUrl ??
    null
  );
}

export function useOnboardingV1Recommendations(input: {
  readonly answers: OnboardingRecommendationAnswers;
  readonly dataSource?: OnboardingV1RecommendationDataSource;
}) {
  const dataSource = input.dataSource ?? browserDataSource;
  const recommendationsQuery = useQuery(
    getRecommendationQueryOptions({
      answers: input.answers,
      dataSource,
    }),
  );

  const recommendationAssetIds = useMemo(
    () => getRecommendationAssetIds(recommendationsQuery.data),
    [recommendationsQuery.data],
  );

  const tokenDataQuery = useQuery({
    ...getTokenDataQueryOptions({
      assetIds: recommendationAssetIds,
      dataSource,
    }),
    enabled: recommendationAssetIds.length > 0,
  });

  const tokenMap = useMemo(
    () =>
      normalizeTokenMap(
        toRecord(tokenDataQuery.data)?.assetsWithBasicMarketData ??
          tokenDataQuery.data,
      ),
    [tokenDataQuery.data],
  );

  const cards = useMemo<readonly OnboardingV1RecommendationCard[]>(
    () =>
      (recommendationsQuery.data?.recommendations ?? []).map(
        (recommendation) => ({
          ...recommendation,
          tokens: deriveTemplateAssetIds(recommendation.strategyData).flatMap(
            (assetId) => {
              const token = tokenMap[assetId]?.token;
              const symbol = token?.symbol?.trim();
              const url = getTokenImageUrl(token);
              return symbol && url ? [{ symbol, url }] : [];
            },
          ),
        }),
      ),
    [recommendationsQuery.data?.recommendations, tokenMap],
  );

  const hasIncompleteRecommendations =
    recommendationsQuery.isSuccess &&
    (cards.length === 0 ||
      cards.some(
        (card) => deriveTemplateAssetIds(card.strategyData).length === 0,
      ));
  const isLoading =
    recommendationsQuery.isPending ||
    (recommendationsQuery.isFetching &&
      recommendationsQuery.data === undefined) ||
    (recommendationAssetIds.length > 0 &&
      (tokenDataQuery.isPending ||
        (tokenDataQuery.isFetching && tokenDataQuery.data === undefined)));
  const isError = recommendationsQuery.isError || hasIncompleteRecommendations;

  const retry = () => {
    void recommendationsQuery.refetch();
  };

  return { cards, isError, isLoading, retry };
}

export function usePrefetchOnboardingV1Recommendations(input: {
  readonly answers: OnboardingRecommendationAnswers;
  readonly dataSource?: OnboardingV1RecommendationDataSource;
}) {
  const queryClient = useQueryClient();
  const dataSource = input.dataSource ?? browserDataSource;

  useEffect(() => {
    // Keep the request alive across the projection-to-picker transition. The
    // picker observes these same keys and reuses both in-flight and cached data.
    void queryClient
      .fetchQuery(
        getRecommendationQueryOptions({
          answers: input.answers,
          dataSource,
        }),
      )
      .then((recommendations) => {
        const assetIds = getRecommendationAssetIds(recommendations);
        if (assetIds.length === 0) {
          return;
        }
        return queryClient.prefetchQuery(
          getTokenDataQueryOptions({ assetIds, dataSource }),
        );
      })
      .catch(() => undefined);
  }, [dataSource, input.answers, queryClient]);
}
